import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query'

import {
  type ExecuteOptions,
  type VariablesArg as ExecuteVariablesArg,
  executeOperation,
  getOperationName,
} from './execute'

type VariablesArg<TVariables> = ExecuteVariablesArg<TVariables, []>

const buildQueryKey = <TVariables>(name: string, variables: TVariables | undefined) =>
  variables === undefined ? ([name] as const) : ([name, variables] as const)

/**
 * Query key convention for graphql() documents: [operationName] or
 * [operationName, variables] — mirrors the legacy generated getKey().
 */
export const getGraphqlQueryKey = <TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  ...[variables]: VariablesArg<TVariables>
) => {
  return buildQueryKey(getOperationName(document), variables)
}

export const getGraphqlInfiniteQueryKey = <TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  ...[variables]: VariablesArg<TVariables>
) => {
  return buildQueryKey(`${getOperationName(document)}.infinite`, variables)
}

const buildQueryOptions = <TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables: TVariables | undefined
) => {
  return {
    queryKey: buildQueryKey(getOperationName(document), variables),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      executeOperation(document, variables, { signal }),
  } satisfies UseQueryOptions<TResult>
}

type UseGraphQLOptions<TResult, TData = TResult> = Omit<
  UseQueryOptions<TResult, Error, TData>,
  'queryKey' | 'queryFn'
>

export const useGraphQL = <TResult, TVariables, TData = TResult>(
  document: TypedDocumentNode<TResult, TVariables>,
  ...args: ExecuteVariablesArg<TVariables, [options?: UseGraphQLOptions<TResult, TData>]>
) => {
  const [variables, options] = args
  return useQuery({
    ...buildQueryOptions(document, variables),
    ...options,
  })
}

export const useGraphQLMutation = <TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  options?: Omit<UseMutationOptions<TResult, Error, TVariables>, 'mutationFn'>,
  executeOptions?: ExecuteOptions
) => {
  return useMutation({
    mutationKey: [getOperationName(document)],
    mutationFn: (variables: TVariables) => executeOperation(document, variables, executeOptions),
    ...options,
  })
}
